import { capitalize as _capitalize } from 'lodash';

import type { ISidebarLink } from '@sine/sine-application-frame';

import { AclAction } from '../team/permission/constants/actions';

export const PERMISSIONS = {
  TEAM_ADMIN: 'TEAM_ADMIN',
  PASS: 'PASS',
  INVITATION: 'INVITATION',
  ASSET: 'ASSET',
  REPORTING: 'REPORTING',
  SITE: 'SITE',
  HOST: 'HOST',
  DISPLAY: 'DISPLAY',
  TEAM: 'TEAM',
};

export const BASE_PATH = '/';
export const TEAM_BASE_PATH = '/team';
export const SITE_DETAILS_BASE_PATH = '/locations/sites/:id';
export const LOCATIONS_BASE_PATH = '/locations';
export const ASSETS_BASE_PATH = '/assets';

export const TEAM_ROUTE_ID = {
  ABOUT: 'about',
  COLLABORATORS: 'collaborators',
  API: 'api',
  CHECKLISTS: 'checklists',
  SAML_SSO: 'saml-sso',
  FEATURES: 'features',
  BILLING: 'billing',
};

export const ROUTE_ID = {
  ACTIVITY: 'activity',
  ASSETS: 'assets',
  ASSET_TYPES: 'asset-types',
  INVITATIONS: 'invitations',
  REPORTS: 'reports',
  MY_PASSES: 'my_passes',
  LOCATIONS: 'locations',
  SITES: 'sites',
  SITE_DETAILS: 'site_detail',
  SITE_DETAILS_GENERAL: 'site_detail_ipad',
  SITE_DETAILS_IPAD: 'site_detail_ipad',
  SITE_DETAILS_PASSES: 'site_detail_passes',
  SITE_DETAILS_FORMS: 'site_detail_forms',
  SITE_DETAILS_FORMS_OUT: 'site_details_forms_out',
  SITE_DETAILS_NOTIFICATIONS: 'site_details_notifications',
  SITE_DETAILS_PRINTING: 'site_details_printing',
  SITE_DETAILS_INTEGRATIONS: 'site_details_integrations',
  SITE_DETAILS_DETAILS: 'site_details_details',
  LOBBIES: 'lobbies',
  LOBBIES_SETTINGS: 'lobbies_settings',
  ZONES: 'zones',
  WATCHLISTS: 'watchlists',
  HOSTS: 'hosts',
  DISPLAYS: 'displays',
  TEAM: 'team',
  TEAM_ABOUT: 'team_about',
  TEAM_COLLABORATORS: 'team_collaborators',
  TEAM_API: 'team_api',
  TEAM_CHECKLISTS: 'team_checklists',
  TEAM_SAML_SSO: 'team_saml_sso',
  TEAM_FEATURES: 'team_features',
  TEAM_BILLING: 'team_billing',
  SIGN_UP: 'signup',
  SIGN_UP_BASIC: 'signup_basic',
  SIGN_UP_FULL: 'signup_full',
  SIGN_UP_USER: 'sign_up_user',
  SETUP_TEAM: 'setup_team',
  CHALLENGE: 'challenge',
  FORGOT_PASSWORD: 'forgot_password',
  RESET_PASSWORD: 'password_reset',
  SIGNUP_CLAIM_ACCOUNT: 'signup_claim_account',
  SIGNUP_CLAIM_HOST: 'signup_claim_host',
  USER_SETTINGS: 'user_settings',
  SETUP_SITE: 'setup_site',
  SETUP_ORGANIZATION: 'setup_organization',
  SIGNUP_SITE: 'signup_site',
  ACCOUNT_VERIFY: 'account_verify',
  LOGIN: 'login',
  LOGIN_SAML: 'login_saml',
  LOGIN_SAML_ERROR: 'login_saml_error',
  SAML_VERIFY_EMAIL: 'saml_verify_email',
  FILE_DOWNLOAD: 'file_download',
  HID_CREDENTIALS_DOWNLOAD: 'hid_credentials_download',
  UPDATE_CREDENTIAL: 'credential-requests',
  OUTLOOK_CONFIRM: 'connection-confirm',
  PASS_STATUS: 'pass_status',
  APPOINTEE_CONFIRM: 'appointee_confirm',
  ICS_CONFIRM: 'ics_confirm',
  ICS_CONFIRM_UPDATE: 'ics_confirm_update',
  PERMISSION_STATUS: 'permission_status',
};

export const ROUTE_PATH = {
  ACTIVITY: `${BASE_PATH}`,
  ASSETS: ASSETS_BASE_PATH,
  ASSET_TYPES: `${ASSETS_BASE_PATH}/asset-types`,
  INVITATIONS: `${BASE_PATH}${ROUTE_ID.INVITATIONS}`,
  REPORTS: `${BASE_PATH}${ROUTE_ID.REPORTS}`,
  MY_PASSES: `${BASE_PATH}my-passes`,
  LOBBIES: `${LOCATIONS_BASE_PATH}/${ROUTE_ID.LOBBIES}`,
  LOBBIES_SETTINGS: `${LOCATIONS_BASE_PATH}/${ROUTE_ID.LOBBIES}/:id`,
  LOCATIONS: `${LOCATIONS_BASE_PATH}`,
  SITES: `${LOCATIONS_BASE_PATH}/${ROUTE_ID.SITES}`,
  SITE_DETAILS: `${SITE_DETAILS_BASE_PATH}`,
  SITE_DETAILS_GENERAL: `${SITE_DETAILS_BASE_PATH}/general`,
  SITE_DETAILS_IPAD: `${SITE_DETAILS_BASE_PATH}/ipad`,
  SITE_DETAILS_PASSES: `${SITE_DETAILS_BASE_PATH}/passes`,
  SITE_DETAILS_FORMS: `${SITE_DETAILS_BASE_PATH}/forms`,
  SITE_DETAILS_FORMS_OUT: `${SITE_DETAILS_BASE_PATH}/forms-out`,
  SITE_DETAILS_NOTIFICATIONS: `${SITE_DETAILS_BASE_PATH}/notifications`,
  SITE_DETAILS_PRINTING: `${SITE_DETAILS_BASE_PATH}/printing`,
  SITE_DETAILS_INTEGRATIONS: `${SITE_DETAILS_BASE_PATH}/integrations`,
  SITE_DETAILS_DETAILS: `${SITE_DETAILS_BASE_PATH}/details`,
  ZONES: `${BASE_PATH}${ROUTE_ID.ZONES}`,
  HOSTS: `${BASE_PATH}${ROUTE_ID.HOSTS}`,
  WATCHLISTS: `${BASE_PATH}${ROUTE_ID.WATCHLISTS}`,
  DISPLAYS: `${BASE_PATH}${ROUTE_ID.DISPLAYS}`,
  TEAM: `${TEAM_BASE_PATH}`,
  TEAM_ABOUT: `${TEAM_BASE_PATH}/${TEAM_ROUTE_ID.ABOUT}`,
  TEAM_COLLABORATORS: `${TEAM_BASE_PATH}/${TEAM_ROUTE_ID.COLLABORATORS}`,
  TEAM_API: `${TEAM_BASE_PATH}/${TEAM_ROUTE_ID.API}`,
  TEAM_CHECKLISTS: `${TEAM_BASE_PATH}/${TEAM_ROUTE_ID.CHECKLISTS}`,
  TEAM_WATCHLISTS: `${TEAM_BASE_PATH}/watchlists`,
  TEAM_SAML_SSO: `${TEAM_BASE_PATH}/${TEAM_ROUTE_ID.SAML_SSO}`,
  TEAM_FEATURES: `${TEAM_BASE_PATH}/${TEAM_ROUTE_ID.FEATURES}`,
  TEAM_BILLING: `${TEAM_BASE_PATH}/${TEAM_ROUTE_ID.BILLING}`,
  SIGN_UP: `${BASE_PATH}${ROUTE_ID.SIGN_UP}`,
  SIGN_UP_BASIC: `${BASE_PATH}${ROUTE_ID.SIGN_UP}/basic`,
  SIGN_UP_FULL: `${BASE_PATH}${ROUTE_ID.SIGN_UP}/full`,
  SIGN_UP_USER: `${BASE_PATH}${ROUTE_ID.SIGN_UP}/user`,
  CHALLENGE: `${BASE_PATH}${ROUTE_ID.CHALLENGE}`,
  FORGOT_PASSWORD: `${BASE_PATH}forgot-password`,
  RESET_PASSWORD: `${BASE_PATH}password-reset`,
  SIGNUP_CLAIM_ACCOUNT: `${BASE_PATH}${ROUTE_ID.SIGN_UP}/claim`,
  SIGNUP_CLAIM_HOST: `${BASE_PATH}${ROUTE_ID.SIGN_UP}/claim-host`,
  SUPPLIER_PROFILE_CLAIM: `${BASE_PATH}claim-supplier-profile`,
  USER_SETTINGS: `${BASE_PATH}user-settings`,
  SETUP_SITE: `${BASE_PATH}setup-site`,
  SETUP_ORGANIZATION: `${BASE_PATH}${ROUTE_ID.SIGN_UP}/organization`,
  SIGNUP_SITE: `${BASE_PATH}${ROUTE_ID.SIGN_UP}/site`,
  SETUP_TEAM: `${BASE_PATH}setup-team`,
  ACCOUNT_VERIFY: `${BASE_PATH}verify`,
  LOGIN: `${BASE_PATH}${ROUTE_ID.LOGIN}`,
  LOGIN_SAML: `${BASE_PATH}saml`,
  LOGIN_SAML_ERROR: `${BASE_PATH}saml/error/:error`,
  SAML_VERIFY_EMAIL: `${BASE_PATH}saml/verify-email`,
  FILE_DOWNLOAD: `${BASE_PATH}file-download/:fileId`,
  HID_CREDENTIALS_DOWNLOAD: `${BASE_PATH}integration/credential/file-download/:fileId`,
  UPDATE_CREDENTIAL: `${BASE_PATH}workplace/credential-requests/:status/:token`,
  OUTLOOK_CONFIRM: `${BASE_PATH}user-settings/connection/confirm`,
  PASS_STATUS: `${BASE_PATH}pass/status/:action/:passId`,
  APPOINTEE_CONFIRM: `${BASE_PATH}appointee/confirm`,
  ICS_CONFIRM: `${BASE_PATH}ics-invite/confirm`,
  ICS_CONFIRM_UPDATE: `${BASE_PATH}ics-invite/confirm-update`,
  PERMISSION_STATUS: `${BASE_PATH}team/permission/status/:action/:teamId`,
  WALLET_DOWNLOAD_WALLET_PASS: `${BASE_PATH}:entity/:entityId/export/:vendor`,
};

export const SIDEBAR_ROUTES: Array<{
  id: string;
  path: string;
  label: string;
  iconName: ISidebarLink['icon'];
  permission?: string;
  state?: string;
  featureFlagged?: boolean;
  launchDarklyFlag?: string;
}> = [
  {
    id: ROUTE_ID.ACTIVITY,
    path: ROUTE_PATH.ACTIVITY,
    label: _capitalize(ROUTE_ID.ACTIVITY),
    iconName: 'activity',
    permission: PERMISSIONS.PASS,
  },
  {
    id: ROUTE_ID.INVITATIONS,
    path: ROUTE_PATH.INVITATIONS,
    label: _capitalize(ROUTE_ID.INVITATIONS),
    iconName: 'calendar',
    permission: PERMISSIONS.INVITATION,
  },
  {
    id: ROUTE_ID.REPORTS,
    path: ROUTE_PATH.REPORTS,
    label: _capitalize(ROUTE_ID.REPORTS),
    iconName: 'reports',
    permission: PERMISSIONS.REPORTING,
  },
  {
    id: ROUTE_ID.ASSETS,
    path: ROUTE_PATH.ASSETS,
    label: 'Assets',
    iconName: 'deliveries',
    permission: PERMISSIONS.ASSET,
    launchDarklyFlag: 'release-asset-management',
  },
  {
    id: ROUTE_ID.MY_PASSES,
    path: ROUTE_PATH.MY_PASSES,
    label: 'My Passes',
    iconName: 'pass',
  },
  {
    id: ROUTE_ID.LOCATIONS,
    path: ROUTE_PATH.LOCATIONS,
    label: _capitalize(ROUTE_ID.LOCATIONS),
    iconName: 'multi-tenant',
    permission: PERMISSIONS.SITE,
  },
  {
    id: ROUTE_ID.ZONES,
    path: ROUTE_PATH.ZONES,
    state: 'zones-list',
    label: _capitalize(ROUTE_ID.ZONES),
    iconName: 'zones',
    permission: AclAction.SITE_UPDATE,
    featureFlagged: true,
  },
  {
    id: ROUTE_ID.WATCHLISTS,
    path: ROUTE_PATH.WATCHLISTS,
    label: _capitalize(ROUTE_ID.WATCHLISTS),
    iconName: 'watchlists',
    permission: PERMISSIONS.TEAM_ADMIN,
  },
  {
    id: ROUTE_ID.HOSTS,
    path: ROUTE_PATH.HOSTS,
    label: _capitalize(ROUTE_ID.HOSTS),
    iconName: 'hosts',
    permission: PERMISSIONS.HOST,
  },
  {
    id: ROUTE_ID.DISPLAYS,
    path: ROUTE_PATH.DISPLAYS,
    label: _capitalize(ROUTE_ID.DISPLAYS),
    iconName: 'ipad',
    permission: PERMISSIONS.DISPLAY,
  },
];

export const HIDDEN_APP_FRAME_ROUTES = [
  { id: ROUTE_ID.OUTLOOK_CONFIRM, path: ROUTE_PATH.OUTLOOK_CONFIRM },
  { id: ROUTE_ID.ACCOUNT_VERIFY, path: ROUTE_PATH.ACCOUNT_VERIFY },
  { id: ROUTE_ID.UPDATE_CREDENTIAL, path: ROUTE_PATH.UPDATE_CREDENTIAL },
  { id: ROUTE_ID.PASS_STATUS, path: ROUTE_PATH.PASS_STATUS },
];

export const PRE_LOGIN_FRAME_ROUTES = [
  { id: ROUTE_ID.SETUP_ORGANIZATION, path: ROUTE_PATH.SETUP_ORGANIZATION },
  { id: ROUTE_ID.SIGNUP_SITE, path: ROUTE_PATH.SIGNUP_SITE, permission: PERMISSIONS.TEAM_ADMIN },
];

/**
 * TODO: Base this on @see SIDEBAR_ROUTES
 */
export const TOP_LEVEL_LINKS = Object.entries({
  '/invitations': 'Invitations',
  '/reports': 'Reports',
  '/assets': 'Assets',
  '/my-passes': 'My Passes',
  '/locations': 'Locations',
  '/zones': 'Zones',
  '/watchlists': 'Watchlists',
  '/hosts': 'Hosts',
  '/displays': 'Displays',
});
